import { CanvasDocument, CanvasDocumentVersion } from "@/types/supabase.ext";

// Block types supported — must match types/blocks.ts BlockType
export type BlockType =
  | "markdown"
  | "risks"
  | "contradictions"
  | "timeline"
  | "image"
  | "table"
  | "callout"
  | "divider"
  | "stakeholders";

// Base block interface (loose typing for store/service layer compatibility)
export interface Block {
  id: string;
  type: BlockType;
  content: Record<string, unknown>;
  order: number;
  title?: string;
  summary?: string;
  citations?: DocumentReference[]; // Block-level source citations (used by markdown blocks)
}

// Specific content interfaces for components that need them
export interface MarkdownBlockContent {
  text: string;
}

// Content item types
export interface Risk {
  id: string;
  title: string;
  severity: "low" | "medium" | "high" | "critical";
  description: string;
  keyActors?: string | null; // populated by risk agent but not yet used in canvas
  mitigation?: string;
  costEstimate?: number;
  documentRefs?: string[]; // IDs referencing documentReferences
}

export interface Contradiction {
  id: string;
  title: string;
  description: string;
  documentRefs?: string[];
}

export interface TimelinePhase {
  id: string;
  name: string;
  startDate?: string;
  endDate?: string;
  status: "upcoming" | "active" | "completed";
}

export interface DocumentReference {
  id: string;
  fileName: string;
  pageNumber?: number;
  sourceFileId?: string; // Link to project_files table
}

// Document content structure (what's stored in JSONB)
export interface CanvasDocumentContent {
  blocks: Block[];
  schemaVersion: 2;
}

// Extended types for UI
export interface CanvasDocumentWithContent extends Omit<CanvasDocument, "content"> {
  content: CanvasDocumentContent;
}

export interface CanvasDocumentVersionWithContent extends Omit<CanvasDocumentVersion, "content"> {
  content: CanvasDocumentContent;
}

// UI state types
export interface CanvasDocumentListItem {
  id: string;
  title: string;
  reportType: "main" | "draft" | "archived";
  updatedAt: string;
  createdAt: string;
  blockCount: number;
}

// Helper to create empty document content
export function createEmptyDocumentContent(): CanvasDocumentContent {
  return {
    blocks: [],
    schemaVersion: 2,
  };
}

// ToC (Table of Contents) types
export interface TocHeading {
  id: string;
  level: number;
  text: string;
}

export interface TocSection {
  heading: TocHeading;
  children: TocHeading[];
}

export interface TocEntry {
  blockId: string;
  blockType: BlockType;
  title: string;
  titleHeading: TocHeading | null;
  sections: TocSection[];
}
